// src/embedded-provider.ts
import { base64urlEncode, stringToBase64url } from "@phantom/base64url";
import { AddressType, PhantomClient, SpendingLimitError } from "@phantom/client";
import {
  parseSignMessageResponse,
  parseTransactionResponse,
  parseToKmsTransaction
} from "@phantom/parsers";
import { randomUUID } from "@phantom/utils";
import { Buffer } from "buffer";
import bs582 from "bs58";

// src/constants.ts
var AUTHENTICATOR_EXPIRATION_TIME_MS = 7 * 24 * 60 * 60 * 1e3;
var AUTHENTICATOR_RENEWAL_WINDOW_MS = 2 * 24 * 60 * 60 * 1e3;
var EMBEDDED_PROVIDER_AUTH_TYPES = ["google", "apple", "phantom"];

// src/chains/SolanaChain.ts
import { EventEmitter } from "eventemitter3";
import { NetworkId } from "@phantom/constants";
import bs58 from "bs58";
import { parseSolanaSignedTransaction } from "@phantom/parsers";
var EmbeddedSolanaChain = class {
  constructor(provider) {
    this.provider = provider;
    this.currentNetworkId = NetworkId.SOLANA_MAINNET;
    this._connected = false;
    this._publicKey = null;
    this.eventEmitter = new EventEmitter();
    this.setupEventListeners();
    this.syncInitialState();
  }
  // Wallet adapter compliant properties
  get connected() {
    return this._connected;
  }
  get publicKey() {
    return this._publicKey;
  }
  ensureConnected() {
    if (!this.provider.isConnected()) {
      throw new Error("Solana chain not available. Ensure SDK is connected.");
    }
  }
  // Standard wallet adapter methods
  async signMessage(message) {
    this.ensureConnected();
    const messageStr = typeof message === "string" ? message : new TextDecoder().decode(message);
    const result = await this.provider.signMessage({
      message: messageStr,
      networkId: this.currentNetworkId
    });
    const signature = typeof result.signature === "string" ? new Uint8Array(bs58.decode(result.signature)) : result.signature;
    return {
      signature,
      publicKey: this._publicKey || ""
    };
  }
  async signTransaction(transaction) {
    this.ensureConnected();
    const result = await this.provider.signTransaction({
      transaction,
      networkId: this.currentNetworkId
    });
    const signedTransaction = parseSolanaSignedTransaction(result.rawTransaction);
    if (!signedTransaction) {
      throw new Error("Failed to parse signed transaction");
    }
    return signedTransaction;
  }
  async signAndSendTransaction(transaction) {
    this.ensureConnected();
    const result = await this.provider.signAndSendTransaction({
      transaction,
      networkId: this.currentNetworkId
    });
    if (!result.hash) {
      throw new Error("Transaction not submitted");
    }
    return { signature: result.hash };
  }
  async signAllTransactions(transactions) {
    this.ensureConnected();
    const results = await Promise.all(transactions.map((tx) => this.signTransaction(tx)));
    return results;
  }
  async signAndSendAllTransactions(transactions) {
    const results = await Promise.all(transactions.map((tx) => this.signAndSendTransaction(tx)));
    return { signatures: results.map((result) => result.signature) };
  }
  connect(_options) {
    if (!this.provider.isConnected()) {
      throw new Error("Provider not connected. Call provider connect first.");
    }
    const addresses = this.provider.getAddresses();
    const solanaAddr = addresses.find((a) => a.addressType === "Solana");
    if (!solanaAddr)
      throw new Error("No Solana address found");
    this.updateConnectionState(true, solanaAddr.address);
    return Promise.resolve({ publicKey: solanaAddr.address });
  }
  async disconnect() {
    return this.provider.disconnect();
  }
  switchNetwork(network) {
    this.currentNetworkId = network === "mainnet" ? NetworkId.SOLANA_MAINNET : NetworkId.SOLANA_DEVNET;
    return Promise.resolve();
  }
  getPublicKey() {
    if (!this.provider.isConnected())
      return Promise.resolve(null);
    const addresses = this.provider.getAddresses();
    const solanaAddr = addresses.find((a) => a.addressType === "Solana");
    return Promise.resolve(solanaAddr?.address || null);
  }
  isConnected() {
    return this._connected && this.provider.isConnected();
  }
  setupEventListeners() {
    this.provider.on("connect", (data) => {
      const solanaAddress = data.addresses?.find((addr) => addr.addressType === "Solana");
      if (solanaAddress) {
        this.updateConnectionState(true, solanaAddress.address);
        this.eventEmitter.emit("connect", solanaAddress.address);
      }
    });
    this.provider.on("disconnect", () => {
      this.updateConnectionState(false, null);
      this.eventEmitter.emit("disconnect");
    });
  }
  syncInitialState() {
    if (this.provider.isConnected()) {
      const addresses = this.provider.getAddresses();
      const solanaAddress = addresses.find((a) => a.addressType === "Solana");
      if (solanaAddress) {
        this.updateConnectionState(true, solanaAddress.address);
      }
    }
  }
  updateConnectionState(connected, publicKey) {
    this._connected = connected;
    this._publicKey = publicKey;
  }
  // Event methods for interface compliance
  on(event, listener) {
    this.eventEmitter.on(event, listener);
  }
  off(event, listener) {
    this.eventEmitter.off(event, listener);
  }
};

// src/chains/EthereumChain.ts
import { NetworkId as NetworkId2, chainIdToNetworkId, networkIdToChainId } from "@phantom/constants";
import { EventEmitter as EventEmitter2 } from "eventemitter3";
var EmbeddedEthereumChain = class {
  constructor(provider) {
    this.provider = provider;
    this.currentNetworkId = NetworkId2.ETHEREUM_MAINNET;
    this._connected = false;
    this._accounts = [];
    this.eventEmitter = new EventEmitter2();
    this.setupEventListeners();
    this.syncInitialState();
  }
  // EIP-1193 compliant properties
  get connected() {
    return this._connected;
  }
  get chainId() {
    const chainId = networkIdToChainId(this.currentNetworkId) || 1;
    return `0x${chainId.toString(16)}`;
  }
  get accounts() {
    return this._accounts;
  }
  ensureConnected() {
    if (!this.provider.isConnected()) {
      throw new Error("Ethereum chain not available. Ensure SDK is connected.");
    }
  }
  async request(args) {
    this.ensureConnected();
    return this.handleEmbeddedRequest(args);
  }
  // Connection methods
  connect() {
    if (!this.provider.isConnected()) {
      throw new Error("Provider not connected. Call provider connect first.");
    }
    const addresses = this.provider.getAddresses();
    const ethAddresses = addresses.filter((a) => a.addressType === "Ethereum").map((a) => a.address);
    this.updateConnectionState(true, ethAddresses);
    return Promise.resolve(ethAddresses);
  }
  async disconnect() {
    await this.provider.disconnect();
  }
  // Standard compliant methods (return raw values)
  async signPersonalMessage(message, address) {
    return await this.request({
      method: "personal_sign",
      params: [message, address]
    });
  }
  async signTypedData(typedData, address) {
    return await this.request({
      method: "eth_signTypedData_v4",
      params: [address, JSON.stringify(typedData)]
    });
  }
  async signTransaction(transaction) {
    let networkId = this.currentNetworkId;
    if (transaction.chainId) {
      const numericChainId = typeof transaction.chainId === "number" ? transaction.chainId : parseInt(transaction.chainId, 16);
      const txNetworkId = chainIdToNetworkId(numericChainId);
      if (txNetworkId) {
        networkId = txNetworkId;
      }
    }
    const result = await this.provider.signTransaction({
      transaction,
      networkId
    });
    return result.rawTransaction;
  }
  async sendTransaction(transaction) {
    if (transaction.chainId) {
      const numericChainId = typeof transaction.chainId === "number" ? transaction.chainId : parseInt(transaction.chainId, 16);
      await this.switchChain(numericChainId);
    }
    const result = await this.provider.signAndSendTransaction({
      transaction,
      networkId: this.currentNetworkId
    });
    if (!result.hash) {
      throw new Error("Transaction not submitted");
    }
    return result.hash;
  }
  switchChain(chainId) {
    const numericChainId = typeof chainId === "string" ? chainId.toLowerCase().startsWith("0x") ? parseInt(chainId, 16) : parseInt(chainId, 10) : chainId;
    const networkId = chainIdToNetworkId(numericChainId);
    if (!networkId) {
      throw new Error(`Unsupported chainId: ${chainId}`);
    }
    this.currentNetworkId = networkId;
    this.eventEmitter.emit("chainChanged", `0x${numericChainId.toString(16)}`);
    return Promise.resolve();
  }
  getChainId() {
    const chainId = networkIdToChainId(this.currentNetworkId);
    return Promise.resolve(chainId || 1);
  }
  async getAccounts() {
    return this.request({ method: "eth_accounts" });
  }
  isConnected() {
    return this._connected && this.provider.isConnected();
  }
  setupEventListeners() {
    this.provider.on("connect", (data) => {
      const ethAddresses = data.addresses?.filter((addr) => addr.addressType === "Ethereum")?.map((addr) => addr.address) || [];
      if (ethAddresses.length > 0) {
        this.updateConnectionState(true, ethAddresses);
        this.eventEmitter.emit("connect", { chainId: this.chainId });
        this.eventEmitter.emit("accountsChanged", ethAddresses);
      }
    });
    this.provider.on("disconnect", () => {
      this.updateConnectionState(false, []);
      this.eventEmitter.emit("disconnect", { code: 4900, message: "Provider disconnected" });
      this.eventEmitter.emit("accountsChanged", []);
    });
  }
  syncInitialState() {
    if (this.provider.isConnected()) {
      const addresses = this.provider.getAddresses();
      const ethAddresses = addresses.filter((a) => a.addressType === "Ethereum").map((a) => a.address);
      if (ethAddresses.length > 0) {
        this.updateConnectionState(true, ethAddresses);
      }
    }
  }
  updateConnectionState(connected, accounts) {
    this._connected = connected;
    this._accounts = accounts;
  }
  async handleEmbeddedRequest(args) {
    switch (args.method) {
      case "personal_sign": {
        const [message, _address] = args.params;
        const result = await this.provider.signEthereumMessage({
          message,
          networkId: this.currentNetworkId
        });
        return result.signature;
      }
      case "eth_signTypedData_v4": {
        const [_typedDataAddress, typedDataStr] = args.params;
        const typedData = JSON.parse(typedDataStr);
        const typedDataResult = await this.provider.signTypedDataV4({
          typedData,
          networkId: this.currentNetworkId
        });
        return typedDataResult.signature;
      }
      case "eth_signTransaction": {
        const [transaction] = args.params;
        const networkIdFromTx = transaction.chainId ? chainIdToNetworkId(
          typeof transaction.chainId === "number" ? transaction.chainId : parseInt(transaction.chainId, 16)
        ) : null;
        const signResult = await this.provider.signTransaction({
          transaction,
          networkId: networkIdFromTx || this.currentNetworkId
        });
        return signResult.rawTransaction;
      }
      case "eth_sendTransaction": {
        const [transaction] = args.params;
        const networkIdFromTx = transaction.chainId ? chainIdToNetworkId(
          typeof transaction.chainId === "number" ? transaction.chainId : parseInt(transaction.chainId, 16)
        ) : null;
        const sendResult = await this.provider.signAndSendTransaction({
          transaction,
          networkId: networkIdFromTx || this.currentNetworkId
        });
        return sendResult.hash;
      }
      case "eth_accounts": {
        const addresses = this.provider.getAddresses();
        const ethAddr = addresses.find((a) => a.addressType === "Ethereum");
        return ethAddr ? [ethAddr.address] : [];
      }
      case "eth_chainId": {
        return `0x${(networkIdToChainId(this.currentNetworkId) || 1).toString(16)}`;
      }
      case "wallet_switchEthereumChain": {
        const [{ chainId }] = args.params;
        const numericChainId = parseInt(chainId, 16);
        await this.switchChain(numericChainId);
        return void 0;
      }
      default:
        throw new Error(`Embedded provider doesn't support method: ${args.method}`);
    }
  }
  // Event methods for interface compliance
  on(event, listener) {
    this.eventEmitter.on(event, listener);
  }
  off(event, listener) {
    this.eventEmitter.off(event, listener);
  }
};

// src/utils/retry.ts
async function retryWithBackoff(operation, operationName, logger, maxRetries = 3, baseDelay = 1e3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.log("EMBEDDED_PROVIDER", `Attempting ${operationName}`, {
        attempt,
        maxRetries
      });
      return await operation();
    } catch (error) {
      lastError = error;
      logger.warn("EMBEDDED_PROVIDER", `${operationName} failed`, {
        attempt,
        maxRetries,
        error: error instanceof Error ? error.message : String(error)
      });
      if (attempt === maxRetries) {
        logger.error("EMBEDDED_PROVIDER", `${operationName} failed after ${maxRetries} attempts`, {
          finalError: error instanceof Error ? error.message : String(error)
        });
        break;
      }
      const delay = baseDelay * Math.pow(2, attempt - 1);
      logger.log("EMBEDDED_PROVIDER", `Retrying ${operationName} in ${delay}ms`, {
        attempt: attempt + 1,
        delay
      });
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

// src/utils/session.ts
function generateSessionId() {
  return "session_" + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + "_" + Date.now();
}

// src/embedded-provider.ts
var EmbeddedProvider = class {
  constructor(config, platform, logger) {
    this.client = null;
    this.walletId = null;
    this.addresses = [];
    this.eventListeners = /* @__PURE__ */ new Map();
    this.logger = logger;
    this.logger.log("EMBEDDED_PROVIDER", "Initializing EmbeddedProvider", { config });
    if (config.embeddedWalletType === "app-wallet") {
      throw new Error("app-wallet type is not currently supported. Please use 'user-wallet' instead.");
    }
    this.config = config;
    this.platform = platform;
    this.storage = platform.storage;
    this.authProvider = platform.authProvider;
    this.phantomAppProvider = platform.phantomAppProvider;
    this.urlParamsAccessor = platform.urlParamsAccessor;
    this.stamper = platform.stamper;
    this.solana = new EmbeddedSolanaChain(this);
    this.ethereum = new EmbeddedEthereumChain(this);
    this.logger.info("EMBEDDED_PROVIDER", "EmbeddedProvider initialized");
  }
  /*
   * Event system methods for listening to provider state changes
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event)?.add(callback);
    this.logger.log("EMBEDDED_PROVIDER", "Event listener added", { event });
  }
  off(event, callback) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      this.logger.log("EMBEDDED_PROVIDER", "Event listener removed", { event });
    }
  }
  emit(event, data) {
    const listeners = this.eventListeners.get(event);
    if (listeners && listeners.size > 0) {
      this.logger.log("EMBEDDED_PROVIDER", "Emitting event", { event, listenerCount: listeners.size, data });
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          this.logger.error("EMBEDDED_PROVIDER", "Event callback error", { event, error });
        }
      });
    }
  }
  /**
   * Get the appropriate address for a given network ID from available addresses
   */
  getAddressForNetwork(networkId) {
    const network = networkId.split(":")[0].toLowerCase();
    let targetAddressType;
    switch (network) {
      case "solana":
        targetAddressType = AddressType.solana;
        break;
      case "eip155":
        targetAddressType = AddressType.ethereum;
        break;
      case "bitcoin":
      case "btc":
        targetAddressType = AddressType.bitcoinSegwit;
        break;
      case "sui":
        targetAddressType = AddressType.sui;
        break;
      default:
        targetAddressType = AddressType.ethereum;
        break;
    }
    const matchingAddress = this.addresses.find(
      (addr) => addr.addressType.toLowerCase() === targetAddressType.toLowerCase()
    );
    return matchingAddress?.address;
  }
  async getAndFilterWalletAddresses(walletId) {
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    const addresses = await retryWithBackoff(
      () => this.client.getWalletAddresses(walletId, void 0, derivationIndex),
      "getWalletAddresses",
      this.logger
    ).catch(async (error) => {
      this.logger.error("EMBEDDED_PROVIDER", "getWalletAddresses failed after retries, disconnecting", {
        walletId,
        error: error.message,
        derivationIndex
      });
      await this.storage.clearSession();
      this.client = null;
      this.walletId = null;
      this.addresses = [];
      throw error;
    });
    return addresses.filter((addr) => this.config.addressTypes.some((type) => type === addr.addressType));
  }
  /*
   * We use this method to make sure the session is not invalid, or there's a different session id in the url.
   * If there's a different one, we delete the current session and start from scratch.
   * This prevents issues where users have stale sessions or URL mismatches after redirects.
   */
  async validateAndCleanSession(session) {
    if (!session)
      return null;
    this.logger.log("EMBEDDED_PROVIDER", "Found existing session, validating", {
      sessionId: session.sessionId,
      status: session.status,
      walletId: session.walletId
    });
    if (session.status !== "completed") {
      const urlSessionId = this.urlParamsAccessor.getParam("session_id");
      if (session.status === "pending" && !urlSessionId) {
        this.logger.warn("EMBEDDED_PROVIDER", "Session mismatch detected - pending session without redirect context", {
          sessionId: session.sessionId,
          status: session.status
        });
        await this.storage.clearSession();
        return null;
      } else if (urlSessionId && urlSessionId !== session.sessionId) {
        this.logger.warn("EMBEDDED_PROVIDER", "Session ID mismatch detected", {
          storedSessionId: session.sessionId,
          urlSessionId
        });
        await this.storage.clearSession();
        return null;
      }
    }
    if (session.status === "completed" && !this.isSessionValid(session)) {
      this.logger.warn(
        "EMBEDDED_PROVIDER",
        "Session invalid due to authenticator expiration, will regenerate keypair",
        {
          sessionId: session.sessionId,
          authenticatorExpiresAt: session.authenticatorExpiresAt,
          currentTime: Date.now(),
          expired: session.authenticatorExpiresAt < Date.now()
        }
      );
      await this.storage.clearSession();
      return null;
    }
    return session;
  }
  /*
   * Shared connection logic for both connect() and autoConnect().
   * Handles existing session validation, redirect resume, and session initialization.
   * Returns ConnectResult if connection succeeds, null if should continue with new auth flow.
   */
  async tryExistingConnection(isAutoConnect) {
    this.logger.log("EMBEDDED_PROVIDER", "Getting existing session");
    let session = await this.storage.getSession();
    session = await this.validateAndCleanSession(session);
    if (!session) {
      this.logger.log("EMBEDDED_PROVIDER", "No existing session found");
      return null;
    }
    if (session.status === "completed") {
      this.logger.info("EMBEDDED_PROVIDER", "Using existing completed session", {
        sessionId: session.sessionId,
        walletId: session.walletId
      });
      await this.initializeClientFromSession(session);
      session.lastUsed = Date.now();
      await this.storage.saveSession(session);
      this.logger.info("EMBEDDED_PROVIDER", "Connection from existing session successful", {
        walletId: this.walletId,
        addressCount: this.addresses.length
      });
      await this.ensureValidAuthenticator();
      const result = {
        walletId: this.walletId,
        addresses: this.addresses,
        status: "completed",
        authUserId: session.authUserId,
        authProvider: session.authProvider
      };
      this.emit("connect", {
        ...result,
        source: "existing-session"
      });
      return result;
    }
    this.logger.log("EMBEDDED_PROVIDER", "No completed session found, checking for redirect resume");
    if (this.authProvider.resumeAuthFromRedirect) {
      const authResult = this.authProvider.resumeAuthFromRedirect(session.authProvider);
      if (authResult) {
        this.logger.info("EMBEDDED_PROVIDER", "Resuming from redirect", {
          walletId: authResult.walletId,
          provider: authResult.provider
        });
        try {
          return await this.completeAuthConnection(authResult);
        } catch (error) {
          if (error instanceof Error && error.message.includes("No session found after redirect") && !isAutoConnect) {
            this.logger.warn(
              "EMBEDDED_PROVIDER",
              "Session missing during redirect resume - will start fresh auth flow",
              {
                error: error.message,
                walletId: authResult.walletId
              }
            );
            await this.storage.clearSession();
            return null;
          }
          throw error;
        }
      }
    }
    return null;
  }
  /*
   * We use this method to validate authentication options before processing them.
   * This ensures only supported auth providers are used and required tokens are present.
   */
  validateAuthOptions(authOptions) {
    if (!EMBEDDED_PROVIDER_AUTH_TYPES.includes(authOptions.provider)) {
      throw new Error(
        `Invalid auth provider: ${authOptions.provider}. Must be ${EMBEDDED_PROVIDER_AUTH_TYPES.join(", ")}`
      );
    }
  }
  /*
   * We use this method to validate if a session is still valid.
   * This checks session status, required fields, and authenticator expiration.
   * Sessions never expire by age - only authenticators expire.
   */
  isSessionValid(session) {
    if (!session) {
      return false;
    }
    if (!session.walletId || !session.organizationId || !session.stamperInfo) {
      this.logger.log("EMBEDDED_PROVIDER", "Session missing required fields", {
        hasWalletId: !!session.walletId,
        hasOrganizationId: !!session.organizationId,
        hasStamperInfo: !!session.stamperInfo
      });
      return false;
    }
    if (session.status !== "completed") {
      this.logger.log("EMBEDDED_PROVIDER", "Session not completed", { status: session.status });
      return false;
    }
    if (!session.authenticatorExpiresAt) {
      this.logger.log("EMBEDDED_PROVIDER", "Session invalid - missing authenticator timing", {
        sessionId: session.sessionId
      });
      return false;
    }
    if (Date.now() >= session.authenticatorExpiresAt) {
      this.logger.log("EMBEDDED_PROVIDER", "Authenticator expired, session invalid", {
        authenticatorExpiresAt: new Date(session.authenticatorExpiresAt).toISOString(),
        now: (/* @__PURE__ */ new Date()).toISOString()
      });
      return false;
    }
    this.logger.log("EMBEDDED_PROVIDER", "Session is valid", {
      sessionId: session.sessionId,
      walletId: session.walletId,
      authenticatorExpires: new Date(session.authenticatorExpiresAt).toISOString()
    });
    return true;
  }
  /*
   * Public method to attempt auto-connection using an existing valid session.
   * This should be called after setting up event listeners to avoid race conditions.
   * Silently fails if no valid session exists, enabling seamless reconnection.
   */
  async autoConnect() {
    try {
      this.logger.log("EMBEDDED_PROVIDER", "Starting auto-connect attempt");
      this.emit("connect_start", { source: "auto-connect" });
      const result = await this.tryExistingConnection(true);
      if (result) {
        this.logger.info("EMBEDDED_PROVIDER", "Auto-connect successful", {
          walletId: result.walletId,
          addressCount: result.addresses.length
        });
        this.emit("connect", {
          ...result,
          source: "auto-connect"
        });
        return;
      }
      this.logger.log("EMBEDDED_PROVIDER", "Auto-connect failed: no valid session found");
      this.emit("connect_error", {
        error: "No valid session found",
        source: "auto-connect"
      });
    } catch (error) {
      this.logger.error("EMBEDDED_PROVIDER", "Auto-connect failed", {
        error: error instanceof Error ? error.message : String(error)
      });
      this.emit("connect_error", {
        error: error instanceof Error ? error.message : "Auto-connect failed",
        source: "auto-connect"
      });
      await this.storage.setShouldClearPreviousSession(true);
    }
  }
  /*
   * We use this method to initialize the stamper and create an organization for new sessions.
   * This is the first step when no existing session is found and we need to set up a new wallet.
   */
  async initializeStamper() {
    this.logger.log("EMBEDDED_PROVIDER", "Initializing stamper");
    await this.stamper.init();
    this.logger.log("EMBEDDED_PROVIDER", "Resetting keypair to avoid conflicts with existing keypairs");
    const stamperInfo = await this.stamper.resetKeyPair();
    this.logger.log("EMBEDDED_PROVIDER", "Stamper initialized with fresh keypair", {
      publicKey: stamperInfo.publicKey,
      keyId: stamperInfo.keyId,
      algorithm: this.stamper.algorithm
    });
    const expiresInMs = AUTHENTICATOR_EXPIRATION_TIME_MS;
    this.logger.info("EMBEDDED_PROVIDER", "Stamper ready for auth flow with fresh keypair", {
      publicKey: stamperInfo.publicKey,
      keyId: stamperInfo.keyId
    });
    return { stamperInfo, expiresInMs };
  }
  async createOrganizationForAppWallet(stamperInfo, expiresInMs) {
    const tempClient = new PhantomClient({
      apiBaseUrl: this.config.apiBaseUrl,
      headers: {
        ...this.platform.analyticsHeaders || {}
      }
    });
    const platformName = this.platform.name || "unknown";
    const shortPubKey = stamperInfo.publicKey.slice(0, 8);
    const organizationName = `${this.config.appId.substring(0, 8)}-${platformName}-${shortPubKey}`;
    this.logger.log("EMBEDDED_PROVIDER", "Creating organization for app-wallet", {
      organizationName,
      publicKey: stamperInfo.publicKey,
      platform: platformName
    });
    const base64urlPublicKey = base64urlEncode(bs582.decode(stamperInfo.publicKey));
    const username = `user-${randomUUID()}`;
    const { organizationId } = await tempClient.createOrganization(organizationName, [
      {
        username,
        role: "ADMIN",
        authenticators: [
          {
            authenticatorName: `auth-${shortPubKey}`,
            authenticatorKind: "keypair",
            publicKey: base64urlPublicKey,
            algorithm: "Ed25519",
            expiresInMs
          }
        ]
      }
    ]);
    this.logger.info("EMBEDDED_PROVIDER", "Organization created for app-wallet", { organizationId });
    return organizationId;
  }
  async connect(authOptions) {
    try {
      this.logger.info("EMBEDDED_PROVIDER", "Starting embedded provider connect", {
        authOptions: {
          provider: authOptions.provider
        }
      });
      this.emit("connect_start", {
        source: "manual-connect",
        authOptions: { provider: authOptions.provider }
      });
      const existingResult = await this.tryExistingConnection(false);
      if (existingResult) {
        this.logger.info("EMBEDDED_PROVIDER", "Manual connect using existing connection", {
          walletId: existingResult.walletId,
          addressCount: existingResult.addresses.length
        });
        this.emit("connect", {
          ...existingResult,
          source: "manual-existing"
        });
        return existingResult;
      }
      this.validateAuthOptions(authOptions);
      this.logger.info(
        "EMBEDDED_PROVIDER",
        "No existing connection available, creating new auth flow with fresh keypair"
      );
      const { stamperInfo, expiresInMs } = await this.initializeStamper();
      const session = await this.handleAuthFlow(stamperInfo.publicKey, stamperInfo, authOptions, expiresInMs);
      if (!session) {
        return {
          addresses: [],
          status: "pending",
          authProvider: authOptions.provider
        };
      }
      if (this.config.embeddedWalletType === "app-wallet") {
        session.lastUsed = Date.now();
        await this.storage.saveSession(session);
      }
      await this.initializeClientFromSession(session);
      await this.ensureValidAuthenticator();
      const result = {
        walletId: this.walletId,
        addresses: this.addresses,
        status: "completed",
        authUserId: session?.authUserId,
        authProvider: session?.authProvider
      };
      this.emit("connect", {
        ...result,
        source: "manual"
      });
      return result;
    } catch (error) {
      this.logger.error("EMBEDDED_PROVIDER", "Connect failed with error", {
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error
      });
      this.emit("connect_error", {
        error: error instanceof Error ? error.message : String(error),
        source: "manual-connect"
      });
      if (error instanceof Error) {
        if (error.message.includes("IndexedDB") || error.message.includes("storage")) {
          throw new Error(
            "Storage error: Unable to access browser storage. Please ensure storage is available and try again."
          );
        }
        if (error.message.includes("network") || error.message.includes("fetch")) {
          throw new Error(
            "Network error: Unable to connect to authentication server. Please check your internet connection and try again."
          );
        }
        if (error.message.includes("JWT") || error.message.includes("jwt")) {
          throw new Error(`JWT Authentication error: ${error.message}`);
        }
        if (error.message.includes("Authentication") || error.message.includes("auth")) {
          throw new Error(`Authentication error: ${error.message}`);
        }
        if (error.message.includes("organization") || error.message.includes("wallet")) {
          throw new Error(`Wallet creation error: ${error.message}`);
        }
        throw error;
      }
      throw new Error(`Embedded wallet connection failed: ${String(error)}`);
    }
  }
  async disconnect(shouldClearPreviousSession = true) {
    const wasConnected = this.client !== null;
    await this.storage.setShouldClearPreviousSession(shouldClearPreviousSession);
    this.logger.log("EMBEDDED_PROVIDER", "Set flag to clear previous session on next login");
    await this.storage.clearSession();
    this.client = null;
    this.walletId = null;
    this.addresses = [];
    this.logger.info("EMBEDDED_PROVIDER", "Disconnected from embedded wallet");
    if (wasConnected) {
      this.emit("disconnect", {
        source: "manual"
      });
    }
  }
  async signMessage(params) {
    if (!this.client || !this.walletId) {
      throw new Error("Not connected");
    }
    await this.ensureValidAuthenticator();
    this.logger.info("EMBEDDED_PROVIDER", "Signing message", {
      walletId: this.walletId,
      message: params.message
    });
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    const rawResponse = await this.client.signUtf8Message({
      walletId: this.walletId,
      message: params.message,
      networkId: params.networkId,
      derivationIndex
    });
    this.logger.info("EMBEDDED_PROVIDER", "Message signed successfully", {
      walletId: this.walletId,
      message: params.message
    });
    return parseSignMessageResponse(rawResponse, params.networkId);
  }
  async signEthereumMessage(params) {
    if (!this.client || !this.walletId) {
      throw new Error("Not connected");
    }
    await this.ensureValidAuthenticator();
    this.logger.info("EMBEDDED_PROVIDER", "Signing message", {
      walletId: this.walletId,
      message: params.message
    });
    const looksLikeHex = (str) => /^0x[0-9a-fA-F]+$/.test(str);
    const normalizedMessage = (() => {
      if (looksLikeHex(params.message)) {
        const hexPayload = params.message.slice(2);
        const normalizedHex = hexPayload.length % 2 === 0 ? hexPayload : `0${hexPayload}`;
        return Buffer.from(normalizedHex, "hex").toString("utf8");
      }
      return params.message;
    })();
    const base64UrlMessage = stringToBase64url(normalizedMessage);
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    const rawResponse = await this.client.ethereumSignMessage({
      walletId: this.walletId,
      message: base64UrlMessage,
      networkId: params.networkId,
      derivationIndex
    });
    this.logger.info("EMBEDDED_PROVIDER", "Message signed successfully", {
      walletId: this.walletId,
      message: params.message
    });
    return parseSignMessageResponse(rawResponse, params.networkId);
  }
  async signTypedDataV4(params) {
    if (!this.client || !this.walletId) {
      throw new Error("Not connected");
    }
    await this.ensureValidAuthenticator();
    this.logger.info("EMBEDDED_PROVIDER", "Signing typed data", {
      walletId: this.walletId,
      typedData: params.typedData
    });
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    const rawResponse = await this.client.ethereumSignTypedData({
      walletId: this.walletId,
      typedData: params.typedData,
      networkId: params.networkId,
      derivationIndex
    });
    this.logger.info("EMBEDDED_PROVIDER", "Typed data signed successfully", {
      walletId: this.walletId
    });
    return parseSignMessageResponse(rawResponse, params.networkId);
  }
  async signTransaction(params) {
    if (!this.client || !this.walletId) {
      throw new Error("Not connected");
    }
    await this.ensureValidAuthenticator();
    this.logger.info("EMBEDDED_PROVIDER", "Signing transaction", {
      walletId: this.walletId,
      networkId: params.networkId
    });
    const parsedTransaction = await parseToKmsTransaction(params.transaction, params.networkId);
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    this.logger.log("EMBEDDED_PROVIDER", "Parsed transaction for signing", {
      walletId: this.walletId,
      transaction: parsedTransaction,
      derivationIndex
    });
    const transactionPayload = parsedTransaction.parsed;
    if (!transactionPayload) {
      throw new Error("Failed to parse transaction: no valid encoding found");
    }
    const account = this.getAddressForNetwork(params.networkId);
    if (!account) {
      throw new Error(`No address found for network ${params.networkId}`);
    }
    const rawResponse = await this.client.signTransaction({
      walletId: this.walletId,
      transaction: transactionPayload,
      networkId: params.networkId,
      derivationIndex,
      account
    });
    this.logger.info("EMBEDDED_PROVIDER", "Transaction signed successfully", {
      walletId: this.walletId,
      networkId: params.networkId,
      rawTransaction: rawResponse.rawTransaction
    });
    return await parseTransactionResponse(rawResponse.rawTransaction, params.networkId);
  }
  async signAndSendTransaction(params) {
    if (!this.client || !this.walletId) {
      throw new Error("Not connected");
    }
    await this.ensureValidAuthenticator();
    this.logger.info("EMBEDDED_PROVIDER", "Signing and sending transaction", {
      walletId: this.walletId,
      networkId: params.networkId
    });
    const parsedTransaction = await parseToKmsTransaction(params.transaction, params.networkId);
    const session = await this.storage.getSession();
    const derivationIndex = session?.accountDerivationIndex ?? 0;
    this.logger.log("EMBEDDED_PROVIDER", "Parsed transaction for signing", {
      walletId: this.walletId,
      transaction: parsedTransaction,
      derivationIndex
    });
    const transactionPayload = parsedTransaction.parsed;
    if (!transactionPayload) {
      throw new Error("Failed to parse transaction: no valid encoding found");
    }
    const account = this.getAddressForNetwork(params.networkId);
    if (!account) {
      throw new Error(`No address found for network ${params.networkId}`);
    }
    let rawResponse;
    try {
      rawResponse = await this.client.signAndSendTransaction({
        walletId: this.walletId,
        transaction: transactionPayload,
        networkId: params.networkId,
        derivationIndex,
        account
      });
    } catch (error) {
      if (error instanceof SpendingLimitError) {
        this.emit("spending_limit_reached", { error });
      }
      throw error;
    }
    this.logger.info("EMBEDDED_PROVIDER", "Transaction signed and sent successfully", {
      walletId: this.walletId,
      networkId: params.networkId,
      hash: rawResponse.hash,
      rawTransaction: rawResponse.rawTransaction
    });
    return await parseTransactionResponse(rawResponse.rawTransaction, params.networkId, rawResponse.hash);
  }
  getAddresses() {
    return this.addresses;
  }
  isConnected() {
    return this.client !== null && this.walletId !== null;
  }
  /*
   * We use this method to route between different authentication flows based on wallet type and auth options.
   * It handles app-wallet creation directly or routes to JWT/redirect authentication for user-wallets.
   * Returns null for redirect flows since they don't complete synchronously.
   */
  async handleAuthFlow(publicKey, stamperInfo, authOptions, expiresInMs) {
    if (this.config.embeddedWalletType === "user-wallet") {
      this.logger.info("EMBEDDED_PROVIDER", "Creating user-wallet, routing authentication", {
        authProvider: authOptions.provider
      });
      if (authOptions.provider === "phantom") {
        return await this.handlePhantomAuth(publicKey, stamperInfo, expiresInMs);
      } else {
        this.logger.info("EMBEDDED_PROVIDER", "Starting redirect-based authentication flow", {
          publicKey,
          provider: authOptions?.provider
        });
        return await this.handleRedirectAuth(publicKey, stamperInfo, authOptions);
      }
    } else {
      this.logger.info("EMBEDDED_PROVIDER", "Creating app-wallet", {
        publicKey
      });
      const organizationId = await this.createOrganizationForAppWallet(stamperInfo, expiresInMs);
      const tempClient = new PhantomClient(
        {
          apiBaseUrl: this.config.apiBaseUrl,
          organizationId,
          headers: {
            ...this.platform.analyticsHeaders || {}
          }
        },
        this.stamper
      );
      const wallet = await tempClient.createWallet(`Wallet ${Date.now()}`);
      const walletId = wallet.walletId;
      const now = Date.now();
      const session = {
        sessionId: generateSessionId(),
        walletId,
        organizationId,
        appId: this.config.appId,
        stamperInfo,
        authProvider: "device",
        // For now app wallets have no auth provider.
        accountDerivationIndex: 0,
        // App wallets default to index 0
        status: "completed",
        createdAt: now,
        lastUsed: now,
        authenticatorCreatedAt: now,
        authenticatorExpiresAt: Date.now() + expiresInMs,
        lastRenewalAttempt: void 0
      };
      await this.storage.saveSession(session);
      this.logger.info("EMBEDDED_PROVIDER", "App-wallet created successfully", { walletId, organizationId });
      return session;
    }
  }
  /*
   * We use this method to handle Phantom app-based authentication for user-wallets.
   * This method uses the PhantomAppProvider to authenticate via the browser extension or mobile app.
   *
   * NOTE: Mobile deeplink support is not yet implemented. If we wanted to support mobile deeplinks,
   * we would:
   * 1. Check if the app provider is available using phantomAppProvider.isAvailable()
   * 2. If not available, generate a deeplink (phantom://auth?...)
   * 3. Save a pending session before opening the deeplink
   * 4. Start a polling mechanism to check for auth completion
   * 5. Update the session when the mobile app completes the auth
   */
  async handlePhantomAuth(publicKey, stamperInfo, expiresInMs) {
    this.logger.info("EMBEDDED_PROVIDER", "Starting Phantom authentication flow");
    const isAvailable = this.phantomAppProvider.isAvailable();
    if (!isAvailable) {
      this.logger.error("EMBEDDED_PROVIDER", "Phantom app not available");
      throw new Error(
        "Phantom app is not available. Please install the Phantom browser extension or mobile app to use this authentication method."
      );
    }
    this.logger.info("EMBEDDED_PROVIDER", "Phantom app detected, proceeding with authentication");
    const sessionId = generateSessionId();
    const authResult = await this.phantomAppProvider.authenticate({
      publicKey,
      appId: this.config.appId,
      sessionId
    });
    this.logger.info("EMBEDDED_PROVIDER", "Phantom authentication completed", {
      walletId: authResult.walletId,
      organizationId: authResult.organizationId
    });
    const effectiveExpiresInMs = authResult.expiresInMs > 0 ? authResult.expiresInMs : expiresInMs;
    const now = Date.now();
    const session = {
      sessionId,
      walletId: authResult.walletId,
      organizationId: authResult.organizationId,
      appId: this.config.appId,
      stamperInfo,
      authProvider: "phantom",
      accountDerivationIndex: authResult.accountDerivationIndex,
      status: "completed",
      createdAt: now,
      lastUsed: now,
      authenticatorCreatedAt: now,
      authenticatorExpiresAt: now + effectiveExpiresInMs,
      lastRenewalAttempt: void 0,
      authUserId: authResult.authUserId
    };
    this.logger.log("EMBEDDED_PROVIDER", "Saving Phantom session");
    await this.storage.saveSession(session);
    return session;
  }
  /*
   * We use this method to handle redirect-based authentication (Google/Apple OAuth).
   * It saves a temporary session before redirecting to prevent losing state during the redirect flow.
   * Session timestamp is updated before redirect to prevent race conditions.
   */
  async handleRedirectAuth(publicKey, stamperInfo, authOptions) {
    this.logger.info("EMBEDDED_PROVIDER", "Using Phantom Connect authentication flow (redirect-based)", {
      provider: authOptions.provider,
      hasRedirectUrl: !!this.config.authOptions.redirectUrl,
      authUrl: this.config.authOptions.authUrl
    });
    const now = Date.now();
    const sessionId = generateSessionId();
    const tempSession = {
      sessionId,
      walletId: `temp-wallet-${now}`,
      // Temporary ID, will be updated after redirect
      organizationId: `temp-org-${now}`,
      // Temporary ID, will be updated after redirect
      appId: this.config.appId,
      stamperInfo,
      authProvider: authOptions.provider,
      accountDerivationIndex: void 0,
      // Will be set when redirect completes
      status: "pending",
      createdAt: now,
      lastUsed: now,
      authenticatorCreatedAt: now,
      authenticatorExpiresAt: now + AUTHENTICATOR_EXPIRATION_TIME_MS,
      lastRenewalAttempt: void 0
    };
    this.logger.log("EMBEDDED_PROVIDER", "Saving temporary session before redirect", {
      sessionId: tempSession.sessionId,
      tempWalletId: tempSession.walletId
    });
    tempSession.lastUsed = Date.now();
    await this.storage.saveSession(tempSession);
    const shouldClearPreviousSession = await this.storage.getShouldClearPreviousSession();
    this.logger.info("EMBEDDED_PROVIDER", "Starting Phantom Connect redirect", {
      publicKey,
      appId: this.config.appId,
      provider: authOptions?.provider,
      authUrl: this.config.authOptions.authUrl,
      clearPreviousSession: shouldClearPreviousSession,
      allowRefresh: !shouldClearPreviousSession
    });
    const authResult = await this.authProvider.authenticate({
      publicKey,
      appId: this.config.appId,
      provider: authOptions?.provider,
      redirectUrl: this.config.authOptions.redirectUrl,
      authUrl: this.config.authOptions.authUrl,
      sessionId,
      // OAuth session management - defaults to allowing refresh unless user explicitly logged out
      clearPreviousSession: shouldClearPreviousSession,
      // true only after logout
      allowRefresh: !shouldClearPreviousSession
      // false only after logout
    });
    if (authResult && "walletId" in authResult) {
      this.logger.info("EMBEDDED_PROVIDER", "Authentication completed after redirect", {
        walletId: authResult.walletId,
        organizationId: authResult.organizationId,
        provider: authResult.provider
      });
      tempSession.walletId = authResult.walletId;
      tempSession.organizationId = authResult.organizationId;
      tempSession.authProvider = authResult.provider || tempSession.authProvider;
      tempSession.accountDerivationIndex = authResult.accountDerivationIndex;
      tempSession.authUserId = authResult.authUserId;
      tempSession.status = "completed";
      tempSession.lastUsed = Date.now();
      if (authResult.expiresInMs > 0) {
        const now2 = Date.now();
        tempSession.authenticatorCreatedAt = now2;
        tempSession.authenticatorExpiresAt = now2 + authResult.expiresInMs;
        this.logger.log("EMBEDDED_PROVIDER", "Updated authenticator expiration from immediate auth response", {
          expiresInMs: authResult.expiresInMs,
          expiresAt: new Date(tempSession.authenticatorExpiresAt).toISOString()
        });
      }
      await this.storage.saveSession(tempSession);
      await this.storage.setShouldClearPreviousSession(false);
      this.logger.log("EMBEDDED_PROVIDER", "Cleared logout flag after successful authentication");
      return tempSession;
    }
    this.logger.info("EMBEDDED_PROVIDER", "Redirect authentication initiated, waiting for redirect completion");
    return null;
  }
  async completeAuthConnection(authResult) {
    const session = await this.storage.getSession();
    if (!session) {
      throw new Error("No session found after redirect - session may have expired");
    }
    session.walletId = authResult.walletId;
    session.authProvider = authResult.provider || session.authProvider;
    session.organizationId = authResult.organizationId;
    session.accountDerivationIndex = authResult.accountDerivationIndex;
    session.authUserId = authResult.authUserId;
    session.status = "completed";
    session.lastUsed = Date.now();
    if (authResult.expiresInMs > 0) {
      const now = Date.now();
      session.authenticatorCreatedAt = now;
      session.authenticatorExpiresAt = now + authResult.expiresInMs;
      this.logger.log("EMBEDDED_PROVIDER", "Updated authenticator expiration from auth response", {
        expiresInMs: authResult.expiresInMs,
        expiresAt: new Date(session.authenticatorExpiresAt).toISOString()
      });
    }
    await this.storage.saveSession(session);
    await this.storage.setShouldClearPreviousSession(false);
    this.logger.log("EMBEDDED_PROVIDER", "Cleared logout flag after successful authentication");
    await this.initializeClientFromSession(session);
    await this.ensureValidAuthenticator();
    return {
      walletId: this.walletId,
      addresses: this.addresses,
      status: "completed",
      authUserId: session.authUserId,
      authProvider: session.authProvider
    };
  }
  /*
   * Ensures the authenticator is valid and performs renewal if needed.
   * The renewal of the authenticator can only happen meanwhile the previous authenticator is still valid.
   */
  async ensureValidAuthenticator() {
    const session = await this.storage.getSession();
    if (!session) {
      throw new Error("No active session found");
    }
    const now = Date.now();
    if (!session.authenticatorExpiresAt) {
      this.logger.warn("EMBEDDED_PROVIDER", "Session missing authenticator timing - treating as invalid session");
      await this.disconnect(false);
      throw new Error("Invalid session - missing authenticator timing");
    }
    const timeUntilExpiry = session.authenticatorExpiresAt - now;
    this.logger.log("EMBEDDED_PROVIDER", "Checking authenticator expiration", {
      expiresAt: new Date(session.authenticatorExpiresAt).toISOString(),
      timeUntilExpiry
    });
    if (timeUntilExpiry <= 0) {
      this.logger.error("EMBEDDED_PROVIDER", "Authenticator has expired, disconnecting");
      await this.disconnect(false);
      throw new Error("Authenticator expired");
    }
  }
  /*
   * We use this method to initialize the PhantomClient and fetch wallet addresses from a completed session.
   * This is the final step that sets up the provider's client state and retrieves available addresses.
   */
  async initializeClientFromSession(session) {
    this.logger.log("EMBEDDED_PROVIDER", "Initializing PhantomClient from session", {
      organizationId: session.organizationId,
      walletId: session.walletId,
      appId: session.appId
    });
    if (!this.stamper.getKeyInfo()) {
      await this.stamper.init();
    }
    this.client = new PhantomClient(
      {
        apiBaseUrl: this.config.apiBaseUrl,
        organizationId: session.organizationId,
        headers: {
          ...this.platform.analyticsHeaders || {},
          ...session.authUserId ? { "x-auth-user-id": session.authUserId } : {}
        }
      },
      this.stamper
    );
    this.walletId = session.walletId;
    this.addresses = await this.getAndFilterWalletAddresses(session.walletId);
  }
};
export {
  AUTHENTICATOR_EXPIRATION_TIME_MS,
  AUTHENTICATOR_RENEWAL_WINDOW_MS,
  EMBEDDED_PROVIDER_AUTH_TYPES,
  EmbeddedEthereumChain,
  EmbeddedProvider,
  EmbeddedSolanaChain,
  generateSessionId,
  retryWithBackoff
};
//# sourceMappingURL=index.mjs.map